import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import AdvancedMedia from '../AdvancedMedia';
import { IImage } from 'tbk-components/src/components/Image';
import Stat, { IStatBarStatProps } from '../Stat';

export interface IFeaturedBoxProps extends IBaseElementProps {
	/**
	 * Select the post type you would like to use
	 */
	postType?: 'webinar' | 'case-study';
	/**
	 * If you turn this on, the box will automatically be filled with the most
	 * recent post, replacing whatever is entered below.
	 */
	useRecentPost?: boolean;
	/**
	 * Featured Box Heading
	 */
	heading?: string;
	/**
	 * Featured Box Sub Heading
	 */
	subheading?: string;
	/**
	 * Featured Text
	 * @control richtext
	 */
	text?: string;
	bulletPoints?: string[];
	image?: IImage;
	/**
	 * Stats
	 */
	stats: IStatBarStatProps[];
	/**
	 * Featured Box Link
	 */
	link?: ILink;
	buttonSize?: 'sm' | 'lg';
	/**
	 * Featured Box Secondary Read More Link
	 */
	readMoreLink?: ILink;
	backgroundStyle?: 'Gradient' | 'Solid';
}

/**
 * Featured Box
 * @block
 * @icon star-filled
 */
const FeaturedBox: React.FC<IFeaturedBoxProps> = ({
	postType,
	useRecentPost = false,
	heading,
	subheading,
	text,
	link,
	bulletPoints = [],
	readMoreLink,
	id,
	className,
	classNames = [],
	backgroundStyle = 'Gradient',
	buttonSize = 'sm',
	image,
	stats = [],
}) => (
	<>
	{(subheading ||
		heading ||
		bulletPoints?.length > 0 ||
		text ||
		link?.label ||
		readMoreLink?.label) && (
	<div id={id} className="calian-featured-box px-xxl-0 px-3">
		<div
			className={cn(
				'calian-featured-box-container',
				'mw-1240px w-100 mx-auto py-4 py-md-6 py-xl-9 px-4 px-md-5 d-flex flex-column rounded-5 overflow-hidden',
				`calian-featured-box--${backgroundStyle.toLowerCase()}`,
				className,
				{
					'calian-featured-box--text-only': !heading && !link?.label,
				},
				...classNames,
			)}
		>
			<div className="calian-featured-box-content d-flex flex-column-reverse flex-md-row justify-content-between gap-4 mx-auto mw-1080px w-100">

					<div
						className={cn(
							'calian-featured-box-copy',
							image?.src && 'mw-md-460px',
						)}
					>
						{subheading && (
							<h5 className="calian-featured-box__sub-heading mb-0 text-white subheading">
								{subheading}
							</h5>
						)}
						{heading && (
							<h2 className="calian-featured-box__heading text-white h3">
								{heading}
							</h2>
						)}
						{bulletPoints?.length > 0 && (
							<ul className="calian-featured-box__bullets list-unstyled d-flex gap-2 flex-wrap">
								{bulletPoints.map((bullet, index) => {
									return (
										<li
											key={index}
											className="calian-featured-box__bullet h6 mb-0 text-white"
										>
											{bullet}
										</li>
									);
								})}
							</ul>
						)}
						{stats?.length > 0 && (
							<ul className="calian-featured-box__stats list-unstyled d-flex gap-md-6 gap-3 flex-wrap">
								{stats.map((stat, index) => {
									return (
										<li
											key={index}
											className="calian-featured-box__stat h6 mb-0 text-white"
										>
											<Stat classNames={['']} {...stat} />
										</li>
									);
								})}
							</ul>
						)}
						{text && (
							<div
								className="calian-featured-box__text mb-0 text-white"
								dangerouslySetInnerHTML={{
									__html: text,
								}}
							/>
						)}
						{(link?.label || readMoreLink?.label) && (
							<div className="calian-featured-box__button-group mt-4 mt-md-6 d-flex align-items-center flex-wrap flex-sm-row gap-2 gap-md-4">
								{link?.label && (
									<Button
										{...link}
										className={cn(
											`calian-featured-box__button btn btn-light`,
											buttonSize === 'sm' && 'btn-sm',
										)}
									/>
								)}
								{readMoreLink?.label && (
									<a
										href={readMoreLink.href}
										target={readMoreLink.target}
										className="text-white mb-0"
									>
										<b className="mb-0">
											{readMoreLink.label}
										</b>
									</a>
								)}
							</div>
						)}
					</div>
				{image?.src && (
					<div className="calian-featured-box__image">
						<AdvancedMedia
							image={image}
							size="4:3"
							classNames={['mw-md-328px rounded-5']}
						/>
					</div>
				)}
			</div>
		</div>
	</div>
	)}
	</>
);

export default FeaturedBox;
